import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/core';

export type MobileHeaderProps = {
  title: string;
  context?: string;
  showBack?: boolean;
  onBack?: () => void;
  trailing?: ReactNode;
  className?: string;
  /**
   * Field shell: solid primary bar + on-primary text (opaque when scroll).
   * Default: surface chrome (EXE / generic).
   */
  tint?: "default" | "field";
};

export function MobileHeader({
  title,
  context,
  showBack = false,
  onBack,
  trailing,
  className,
  tint = "default",
}: MobileHeaderProps) {
  const field = tint === "field";

  return (
    <header
      className={cn(
        'sticky top-0 z-[var(--z-sticky)] border-b',
        'pt-[env(safe-area-inset-top)]',
        field
          ? cn(
              'border-primary bg-primary text-on-primary',
              /* Ghost IconButtons (back / bell / filter) on primary chrome */
              '[&_button]:text-on-primary [&_button:hover]:bg-transparent [&_button:hover]:opacity-85 [&_button:active]:opacity-70 [&_button:hover]:text-on-primary',
            )
          : 'border-border/80 bg-surface text-text',
        className,
      )}
    >
      <div className="flex min-h-[var(--control-height-touch)] items-center gap-1 px-2">
        {showBack ? (
          <IconButton
            tooltip={false}
            label="Quay lại"
            icon={<ChevronLeft />}
            size="touch"
            onClick={onBack}
          />
        ) : (
          <span className="w-[var(--control-height-touch)] shrink-0" aria-hidden />
        )}
        <div className="min-w-0 flex-1 text-center">
          <h1
            className={cn(
              'text-h3 m-0 truncate font-bold',
              field ? 'text-on-primary' : 'text-text',
            )}
          >
            {title}
          </h1>
          {context ? (
            <p
              className={cn(
                'text-caption m-0 truncate',
                field ? 'text-on-primary/85' : 'text-text-muted',
              )}
            >
              {context}
            </p>
          ) : null}
        </div>
        <div className="flex min-w-[var(--control-height-touch)] justify-end">{trailing}</div>
      </div>
    </header>
  );
}
